import React from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import ProposedMetricCard from '../blocks/ProposedMetricCard'
import RemoveMetricCard from '../blocks/RemoveMetricCard'
import ProposedRubricCard from '../blocks/ProposedRubricCard'
import ProposedPromptCard from '../blocks/ProposedPromptCard'

const MONO = "'SF Mono', 'Fira Code', 'Cascadia Code', 'JetBrains Mono', Menlo, Consolas, monospace"

function formatTime (iso) {
  if (!iso) return ''
  const d = new Date(iso)
  if (isNaN(d.getTime())) return ''
  return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
}

const useStyles = makeStyles(() => ({
  messageHeader: {
    display: 'flex',
    alignItems: 'baseline',
    gap: '8px',
    paddingBottom: '5px',
    marginBottom: '8px',
    borderBottom: '1px solid var(--color-ai-200, rgba(169,88,255,0.18))'
  },
  senderLabel: {
    fontSize: '10px',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.6px',
    fontFamily: MONO
  },
  userLabel: { color: 'var(--color-neutral-700)' },
  agentLabel: { color: 'var(--color-ai-600)' },
  timestamp: {
    fontSize: '10px',
    color: 'var(--color-neutral-400)',
    fontWeight: 500,
    fontFamily: MONO,
    letterSpacing: '0.2px'
  },
  userHeaderRow: { justifyContent: 'flex-start' },
  agentHeaderRow: { justifyContent: 'flex-end' },
  humanMessage: {
    backgroundColor: 'rgba(255, 255, 255, 0.88)',
    padding: '10px 14px',
    fontSize: '12.5px',
    lineHeight: 1.55,
    wordBreak: 'break-word',
    borderRadius: '4px'
  },
  assistantMessage: {
    fontSize: '12.5px',
    lineHeight: 1.55,
    wordBreak: 'break-word',
    '& pre': {
      backgroundColor: 'rgba(255, 255, 255, 0.85)',
      padding: '8px 12px',
      borderRadius: '4px',
      border: '1px solid var(--color-ai-200, rgba(169,88,255,0.15))',
      overflow: 'auto',
      fontSize: '11.5px',
      fontFamily: MONO
    },
    '& code': {
      backgroundColor: 'rgba(255, 255, 255, 0.7)',
      padding: '1px 5px',
      borderRadius: '3px',
      fontSize: '11.5px',
      fontFamily: MONO,
      border: '1px solid var(--color-ai-200, rgba(169,88,255,0.12))'
    },
    '& pre code': {
      backgroundColor: 'transparent',
      padding: 0,
      border: 'none'
    },
    '& p': { margin: '4px 0' },
    '& ul, & ol': { margin: '4px 0', paddingLeft: '20px' },
    '& li': { marginBottom: '2px' }
  },
  compaction: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '6px 0',
    color: 'var(--color-neutral-500)'
  },
  compactionLine: {
    flex: 1,
    height: '1px',
    background: 'var(--color-neutral-200)'
  },
  compactionLabel: {
    fontSize: '10.5px',
    fontFamily: MONO,
    letterSpacing: '0.3px'
  },
  systemMessage: {
    textAlign: 'center',
    fontSize: '11px',
    opacity: 0.5,
    padding: '4px 0'
  }
}))

function partitionToolCalls (toolCalls) {
  return {
    metrics: toolCalls.filter(c => c.name === 'propose_metric' && c.args?.name),
    metricRemovals: toolCalls.filter(c => c.name === 'remove_metric' && c.args?.name),
    rubrics: toolCalls.filter(c => c.name === 'propose_rubric' && typeof c.args?.rubric === 'string'),
    prompts: toolCalls.filter(c => c.name === 'propose_prompt' && typeof c.args?.prompt === 'string')
  }
}

function CompactionMessage ({ message }) {
  const classes = useStyles()
  const meta = message.metadata || {}
  const n = meta.messagesSummarized
  const label = meta.kind === 'trim'
    ? (n ? `Earlier ${n} messages dropped to stay under the model's input limit` : 'Earlier messages dropped')
    : (n ? `Earlier context compacted \u2014 ${n} messages summarized` : 'Earlier context compacted')

  return (
    <div className={classes.compaction}>
      <span className={classes.compactionLine} />
      <span className={classes.compactionLabel}>{label}</span>
      <span className={classes.compactionLine} />
    </div>
  )
}

CompactionMessage.propTypes = { message: PropTypes.object.isRequired }

function UserMessage ({ message }) {
  const classes = useStyles()
  const content = typeof message.content === 'string'
    ? message.content
    : message.content?.[0]?.text || ''

  return (
    <div>
      <div className={`${classes.messageHeader} ${classes.userHeaderRow}`}>
        <span className={`${classes.senderLabel} ${classes.userLabel}`}>You</span>
        <span className={classes.timestamp}>{formatTime(message.createdAt)}</span>
      </div>
      <div className={classes.humanMessage}>{content}</div>
    </div>
  )
}

UserMessage.propTypes = { message: PropTypes.object.isRequired }

function AssistantMessage ({ message, onApplyProposal, autoAccept }) {
  const classes = useStyles()

  const content = typeof message.content === 'string'
    ? message.content
    : Array.isArray(message.content)
      ? message.content.filter(c => c.type === 'text').map(c => c.text).join('')
      : ''

  const toolCalls = Array.isArray(message.toolCalls) ? message.toolCalls : []
  const { metrics, metricRemovals, rubrics, prompts } = partitionToolCalls(toolCalls)

  const hasContent = !!content
  const hasCards = metrics.length || metricRemovals.length || rubrics.length || prompts.length
  if (!hasContent && !hasCards) return null

  return (
    <div>
      <div className={`${classes.messageHeader} ${classes.agentHeaderRow}`}>
        <span className={classes.timestamp}>{formatTime(message.createdAt)}</span>
        <span className={`${classes.senderLabel} ${classes.agentLabel}`}>Designer</span>
      </div>
      {hasContent && (
        <div className={classes.assistantMessage}>
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
        </div>
      )}
      {metrics.map((call, idx) => (
        <ProposedMetricCard
          key={call.id || `metric-${idx}`}
          toolCallId={call.id}
          args={call.args}
          onApply={onApplyProposal}
          autoAccept={autoAccept}
        />
      ))}
      {metricRemovals.map((call, idx) => (
        <RemoveMetricCard
          key={call.id || `remove-${idx}`}
          toolCallId={call.id}
          args={call.args}
          onApply={onApplyProposal}
          autoAccept={autoAccept}
        />
      ))}
      {rubrics.map((call, idx) => (
        <ProposedRubricCard
          key={call.id || `rubric-${idx}`}
          toolCallId={call.id}
          args={call.args}
          onApply={onApplyProposal}
          autoAccept={autoAccept}
        />
      ))}
      {prompts.map((call, idx) => (
        <ProposedPromptCard
          key={call.id || `prompt-${idx}`}
          toolCallId={call.id}
          args={call.args}
          onApply={onApplyProposal}
          autoAccept={autoAccept}
        />
      ))}
    </div>
  )
}

AssistantMessage.propTypes = {
  message: PropTypes.object.isRequired,
  onApplyProposal: PropTypes.func,
  autoAccept: PropTypes.bool
}

function ChatMessage ({ message, onApplyProposal, autoAccept }) {
  const classes = useStyles()

  if (message.role === 'compaction') return <CompactionMessage message={message} />
  if (message.name === 'system') {
    return (
      <div className={classes.systemMessage}>
        {typeof message.content === 'string' ? message.content : ''}
      </div>
    )
  }
  if (message.role === 'user') return <UserMessage message={message} />
  if (message.role === 'assistant') {
    return <AssistantMessage message={message} onApplyProposal={onApplyProposal} autoAccept={autoAccept} />
  }
  return null
}

ChatMessage.propTypes = {
  message: PropTypes.object.isRequired,
  onApplyProposal: PropTypes.func,
  autoAccept: PropTypes.bool
}

export default ChatMessage
